import React, { useEffect, useState } from 'react';
import axios from 'axios';
import {
  Users, Plus, Edit2, Trash2, UserCheck, UserX,
  X, Check, Loader, CheckCircle, AlertCircle,
  Mail, Phone, Shield, Building2
} from 'lucide-react';
import { getUtilisateurs, updateUtilisateur, deleteUtilisateur } from '../services/api';
import { useBoutique } from '../context/BoutiqueContext';
import { useAuth } from '../context/AuthContext';
import BoutiqueSelector from '../components/BoutiqueSelector';
import './Utilisateurs.css';

const ROLES = {
  'admin':           { label:'Admin (legacy)',      color:'#dc2626', bg:'#fee2e2' },
  'admin-entreprise':{ label:'Admin Entreprise',    color:'#7c3aed', bg:'#ede9fe' },
  'admin-pro':       { label:'Admin Pro',           color:'#db2777', bg:'#fce7f3' },
  'admin-boutique':  { label:'Admin Boutique',      color:'#ea580c', bg:'#ffedd5' },
  'gestionnaire':    { label:'Gestionnaire',        color:'#2563eb', bg:'#dbeafe' },
  'caissier':        { label:'Caissier',            color:'#16a34a', bg:'#dcfce7' },
};

const initials = (u) => ((u.prenom?.[0] || '') + (u.nom?.[0] || '')).toUpperCase() || '?';

export default function Utilisateurs() {
  const { user }                        = useAuth();
  const { selectedBoutiqueId, boutiques } = useBoutique();

  const [users,     setUsers]     = useState([]);
  const [filtered,  setFiltered]  = useState([]);
  const [loading,   setLoading]   = useState(true);
  const [submitting,setSubmitting]= useState(false);
  const [toast,     setToast]     = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [editing,   setEditing]   = useState(null);
  const [formData,  setFormData]  = useState({
    nom:'', prenom:'', email:'', telephone:'', password:'', role:'caissier', boutiqueIdModal:'', peutNegocier:false
  });

  useEffect(() => { load(); }, [selectedBoutiqueId]); // eslint-disable-line

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await getUtilisateurs();
      const list = data.data || [];
      setUsers(list);
      applyFilter(list);
    } catch { showToast('error', 'Erreur lors du chargement'); }
    finally { setLoading(false); }
  };

  const applyFilter = (list = users) => {
    if (!['admin-entreprise', 'super-admin'].includes(user?.role)) {
      setFiltered(list); return;
    }
    if (!selectedBoutiqueId || selectedBoutiqueId === 'all') setFiltered(list);
    else setFiltered(list.filter(u => u.boutiqueId === selectedBoutiqueId));
  };

  useEffect(() => { applyFilter(); }, [selectedBoutiqueId, users]); // eslint-disable-line

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 3500);
  };

  const resetForm = () => {
    setFormData({ nom:'', prenom:'', email:'', telephone:'', password:'', role:'caissier', boutiqueIdModal: selectedBoutiqueId || '', peutNegocier:false });
    setEditing(null);
  };

  const openNew = () => {
    resetForm();
    setFormData(f => ({ ...f, boutiqueIdModal: selectedBoutiqueId || '' }));
    setShowModal(true);
  };

  const openEdit = (u) => {
    setEditing(u);
    setFormData({
      nom: u.nom, prenom: u.prenom, email: u.email, telephone: u.telephone, password: '',
      role: u.role, boutiqueIdModal: u.boutiqueId || '', peutNegocier: !!u.peutNegocier
    });
    setShowModal(true);
  };

  const closeModal = () => { setShowModal(false); resetForm(); };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      if (editing) {
        const payload = { ...formData };
        if (!payload.password) delete payload.password;
        await updateUtilisateur(editing._id, payload);
        showToast('success', 'Utilisateur modifié avec succès');
      } else {
        const payload = { ...formData };
        if (payload.boutiqueIdModal) payload.boutiqueId = payload.boutiqueIdModal;
        delete payload.boutiqueIdModal;
        await axios.post('/api/auth/register', payload);
        showToast('success', 'Utilisateur créé avec succès');
      }
      closeModal();
      load();
    } catch (err) { showToast('error', err.response?.data?.message || 'Erreur'); }
    finally { setSubmitting(false); }
  };

  const handleToggle = async (u) => {
    try {
      await updateUtilisateur(u._id, { actif: !u.actif });
      showToast('success', `Utilisateur ${u.actif ? 'désactivé' : 'activé'}`);
      load();
    } catch { showToast('error', 'Erreur lors de la modification'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cet utilisateur ?')) return;
    try {
      await deleteUtilisateur(id);
      showToast('success', 'Utilisateur supprimé');
      load();
    } catch { showToast('error', 'Erreur lors de la suppression'); }
  };

  const actifs   = filtered.filter(u => u.actif).length;
  const inactifs = filtered.filter(u => !u.actif).length;
  const isAdmin  = ['admin-entreprise', 'super-admin'].includes(user?.role);

  if (loading) return (
    <div className="ur-loading"><Loader className="ur-spinner" size={26}/><span>Chargement…</span></div>
  );

  return (
    <div className="ur-page">

      {/* Toast */}
      {toast && (
        <div className={`ur-toast ur-toast--${toast.type}`}>
          {toast.type === 'success' ? <CheckCircle size={13}/> : <AlertCircle size={13}/>}
          {toast.text}
        </div>
      )}

      {/* ── Header ── */}
      <div className="ur-header">
        <div className="ur-header-left">
          <div className="ur-header-icon"><Users size={18}/></div>
          <div>
            <h1 className="ur-header-title">Utilisateurs</h1>
            <p className="ur-header-sub">Gérez les accès et rôles de votre entreprise</p>
          </div>
        </div>
        <div className="ur-header-right">
          {isAdmin && <BoutiqueSelector />}
          <button className="ur-new-btn" onClick={openNew}>
            <Plus size={15}/> Nouvel utilisateur
          </button>
        </div>
      </div>

      <div className="ur-content">

        {/* KPI */}
        <div className="ur-kpi-row">
          {[
            { label:'Total',    value: filtered.length, color:'#6366f1', bg:'#eef2ff', icon: Users     },
            { label:'Actifs',   value: actifs,          color:'#16a34a', bg:'#dcfce7', icon: UserCheck  },
            { label:'Inactifs', value: inactifs,        color:'#dc2626', bg:'#fee2e2', icon: UserX      },
          ].map((k, i) => {
            const Icon = k.icon;
            return (
              <div key={i} className="ur-kpi" style={{ borderTopColor: k.color }}>
                <div className="ur-kpi-icon" style={{ background: k.bg, color: k.color }}><Icon size={14}/></div>
                <div className="ur-kpi-val">{k.value}</div>
                <div className="ur-kpi-label">{k.label}</div>
              </div>
            );
          })}
        </div>

        {/* Table */}
        {filtered.length === 0 ? (
          <div className="ur-empty">
            <Users size={28}/>
            <span>Aucun utilisateur trouvé</span>
          </div>
        ) : (
          <div className="ur-card">
            <table className="ur-table">
              <thead>
                <tr>
                  <th>Utilisateur</th>
                  <th>Email</th>
                  <th>Téléphone</th>
                  <th>Rôle</th>
                  <th>Statut</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map(u => {
                  const role = ROLES[u.role] || { label: u.role, color:'#64748b', bg:'#f1f5f9' };
                  return (
                    <tr key={u._id} style={{ opacity: u.actif ? 1 : 0.55 }}>
                      <td>
                        <div className="ur-user-cell">
                          <div className="ur-av" style={{ background: role.bg, color: role.color }}>
                            {initials(u)}
                          </div>
                          <div>
                            <div className="ur-user-name">{u.prenom} {u.nom}</div>
                            {u.boutiqueId && boutiques.find(b => b._id === u.boutiqueId) && (
                              <div className="ur-user-boutique">
                                <Building2 size={10}/> {boutiques.find(b => b._id === u.boutiqueId)?.nom}
                              </div>
                            )}
                          </div>
                        </div>
                      </td>
                      <td>
                        <span className="ur-contact"><Mail size={11}/> {u.email || '—'}</span>
                      </td>
                      <td>
                        <span className="ur-contact"><Phone size={11}/> {u.telephone || '—'}</span>
                      </td>
                      <td>
                        <span className="ur-role-badge" style={{ background: role.bg, color: role.color }}>
                          <Shield size={10}/> {role.label}
                        </span>
                        {u.peutNegocier && ['caissier', 'gestionnaire'].includes(u.role) && (
                          <span className="ur-neg-badge" title="Peut dépasser le prix de vente listé">
                            Négociateur
                          </span>
                        )}
                      </td>
                      <td>
                        {u.actif
                          ? <span className="ur-status ur-status--on">Actif</span>
                          : <span className="ur-status ur-status--off">Inactif</span>}
                      </td>
                      <td>
                        <div className="ur-actions">
                          <button className="ur-btn ur-btn--edit" onClick={() => openEdit(u)} title="Modifier">
                            <Edit2 size={13}/>
                          </button>
                          <button
                            className={`ur-btn ${u.actif ? 'ur-btn--warn' : 'ur-btn--ok'}`}
                            onClick={() => handleToggle(u)}
                            title={u.actif ? 'Désactiver' : 'Activer'}
                          >
                            {u.actif ? <UserX size={13}/> : <UserCheck size={13}/>}
                          </button>
                          <button className="ur-btn ur-btn--del" onClick={() => handleDelete(u._id)} title="Supprimer">
                            <Trash2 size={13}/>
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* ── Modal ── */}
      {showModal && (
        <>
          <div className="ur-overlay" onClick={closeModal}/>
          <div className="ur-modal">
            <div className="ur-modal-head">
              <div>
                <h2 className="ur-modal-title">
                  {editing ? <><Edit2 size={15}/> Modifier l'utilisateur</> : <><Plus size={15}/> Nouvel utilisateur</>}
                </h2>
                {editing && <p className="ur-modal-sub">{editing.prenom} {editing.nom}</p>}
              </div>
              <button className="ur-modal-close" onClick={closeModal}><X size={16}/></button>
            </div>

            <form onSubmit={handleSubmit}>
              <div className="ur-modal-body">
                <div className="ur-form-row2">
                  <div className="ur-field">
                    <label>Prénom *</label>
                    <input type="text" required autoFocus
                      value={formData.prenom}
                      onChange={e => setFormData({...formData, prenom: e.target.value})}/>
                  </div>
                  <div className="ur-field">
                    <label>Nom *</label>
                    <input type="text" required
                      value={formData.nom}
                      onChange={e => setFormData({...formData, nom: e.target.value})}/>
                  </div>
                </div>

                <div className="ur-field">
                  <label>Email *</label>
                  <input type="email" required
                    value={formData.email}
                    onChange={e => setFormData({...formData, email: e.target.value})}/>
                </div>

                <div className="ur-form-row2">
                  <div className="ur-field">
                    <label>Téléphone *</label>
                    <input type="tel" required
                      value={formData.telephone}
                      onChange={e => setFormData({...formData, telephone: e.target.value})}/>
                  </div>
                  <div className="ur-field">
                    <label>Rôle *</label>
                    <select required
                      value={formData.role}
                      onChange={e => setFormData({...formData, role: e.target.value})}>
                      <option value="caissier">Caissier</option>
                      <option value="gestionnaire">Gestionnaire</option>
                      <option value="admin-boutique">Admin Boutique (Gérant)</option>
                      <option value="admin-pro">Admin Pro (Multi-boutiques)</option>
                      <option value="admin-entreprise">Admin Entreprise</option>
                      <option value="admin">Administrateur (legacy)</option>
                    </select>
                  </div>
                </div>

                {isAdmin && (
                  <div className="ur-field">
                    <label>Boutique <span className="ur-opt">(optionnel)</span></label>
                    <select
                      value={formData.boutiqueIdModal}
                      onChange={e => setFormData({...formData, boutiqueIdModal: e.target.value})}>
                      <option value="">Aucune boutique spécifique</option>
                      {boutiques.map(b => (
                        <option key={b._id} value={b._id}>{b.nom}</option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Négociateur — visible uniquement pour caissier/gestionnaire (les admins l'ont implicitement) */}
                {['caissier', 'gestionnaire'].includes(formData.role) && (
                  <div className="ur-field ur-field-negociateur">
                    <label className="ur-toggle-label">
                      <input type="checkbox"
                        checked={formData.peutNegocier}
                        onChange={e => setFormData({...formData, peutNegocier: e.target.checked})}/>
                      <span className="ur-toggle-text">
                        <strong>Caissier négociateur</strong>
                        <span className="ur-toggle-hint">
                          Peut dépasser le prix de vente listé. Le prix de revient reste un plancher.
                        </span>
                      </span>
                    </label>
                  </div>
                )}

                <div className="ur-field">
                  <label>
                    Mot de passe {editing
                      ? <span className="ur-opt">— laisser vide pour ne pas modifier</span>
                      : '*'}
                  </label>
                  <input type="password" minLength="6"
                    required={!editing}
                    value={formData.password}
                    onChange={e => setFormData({...formData, password: e.target.value})}/>
                </div>
              </div>

              <div className="ur-modal-footer">
                <button type="button" className="ur-btn-cancel" onClick={closeModal}>Annuler</button>
                <button type="submit" className="ur-btn-confirm" disabled={submitting}>
                  {submitting
                    ? <><Loader size={13} className="ur-spin"/> Enregistrement…</>
                    : <><Check size={13}/> {editing ? 'Enregistrer' : 'Créer'}</>}
                </button>
              </div>
            </form>
          </div>
        </>
      )}
    </div>
  );
}
